<template>
  <div class="trigger-action-card-container">
    <BCard
      border
    >
      <div class="header">
        <div>
          {{ $t('trigger.action') }}
        </div>
        <div
          class="more-menu"
        >
          <BMoreMenu
            v-model="isMenuVisible"
            left
          >
            <template #default>
              <BBtn
                flat
                fit
                position="center"
                size="small"
                :disabled="!editable"
                @click="() => clickMenuButton('form')"
              >
                {{ $t('general.edit.text') }}
              </BBtn>
              <BBtn
                flat
                fit
                position="center"
                size="small"
                @click="() => clickMenuButton('confirm')"
              >
                {{ $t('trigger.actionSetting.detail') }}
              </BBtn>
            </template>
          </BMoreMenu>
        </div>
      </div>
      <div class="body">
        <BListItem class="list-item">
          <template #header>
            <span>{{ $t('trigger.actionSetting.actionType') }}</span>
          </template>
          <div class="mt-100">
            {{ actionDescription }}
          </div>
        </BListItem>
      </div>
      <TriggerActionCardSettings
        :trigger-action="triggerAction"
        :forms="forms"
      />
      <div class="approval-section">
        <TriggerCardSettingRow :label="$t('trigger.actionSetting.approveUserSelect')">
          {{ props.triggerAction?.approveUserId
            ? $t('trigger.actionSetting.approvalStatusWith')
            : $t('trigger.actionSetting.approvalStatusWithout') }}
        </TriggerCardSettingRow>
      </div>
      <div
        v-if="approveUser"
        class="footer"
      >
        <TriggerCardSettingRow :label="$t('trigger.actionSetting.approveUser')">
          <BUserTooltip
            class="approve-user-avatar"
            :image-url="approveUser.avatarUrl"
            :name="approveUser.name"
            :user-id="approveUser.id"
            :size="30"
          />
          <span class="ml-100">{{ approveUser.name }}</span>
        </TriggerCardSettingRow>
      </div>
    </BCard>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { TriggerActionBody, TriggerActionForm } from '@/api/openapi';
import { useUsers } from '@/composable/user/user/users';
import TriggerActionCardSettings from './TriggerActionCardSettings.vue';
import TriggerCardSettingRow from './TriggerCardSettingRow.vue';
import { TTriggerActionFormMode } from './types';

type TProps = {
  triggerAction: TriggerActionBody | null;
  forms?: TriggerActionForm[];
  editable: boolean;
};
type TEmits = {
  'click:setting': [mode: TTriggerActionFormMode];
};
const props = withDefaults(defineProps<TProps>(), {
  forms: () => [],
});
const emit = defineEmits<TEmits>();

const i18n = useI18n();
const isMenuVisible = ref<boolean>();

const clickMenuButton = (mode: TTriggerActionFormMode) => {
  isMenuVisible.value = false;
  emit('click:setting', mode);
};

const actionDescription = computed(() => {
  let description = '';
  switch (props.triggerAction?.actionType) {
    case 'sequence_register':
    case 'direct_mail_unsubscribe':
    case 'direct_mail_subscribe':
    case 'bulk_mail_unsubscribe':
    case 'bulk_mail_subscribe':
    case 'lead_stage_change':
      description = i18n.t(`trigger.actionTypes.${props.triggerAction?.actionType}`);
      break;
  }
  return description;
});

// wait キーをカード専用に分離し、ページ側で「カード表示に必要なデータの取得中」を判別できるようにする。
const { users } = useUsers(() => true, () => 'TriggerActionCard');
const approveUser = computed(() => {
  return users.value.find((user) => user.id === props.triggerAction?.approveUserId);
});
</script>

<style lang="scss" scoped>
.trigger-action-card-container {
  :deep(.card) {
    padding: 0;

    .card-body {
      .header {
        font-weight: bold;
        display: flex;
        align-items: center;
        justify-content: space-between;
        background-color: $charcoal;
        color: $textcolor-white;
        padding: 8px 20px;
        border-radius: 6px 6px 0 0;

        .more-menu {
          .b-icon {
            color: $textcolor-white;
          }
        }
      }

      .body {
        padding: 20px;
      }

      .approval-section {
        padding: 12px 20px;
        border-top: 1px solid $bdcolor-light;
      }

      .footer {
        padding: 12px 20px;
        border-top: 1px solid $bdcolor-light;

        .approve-user-avatar {
          display: inline-block;
          vertical-align: middle;
        }
      }
    }
  }
}
</style>
